import React from "react";
import * as Sentry from "@sentry/react";

// Catches render/runtime errors anywhere below it and shows a recovery screen
// instead of a blank white page (which previously forced users to close & reload).
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // Surface the error for debugging; never swallow silently.
    console.error("App error boundary caught:", error, info?.componentStack);
    // Report to Sentry in production (no-op when Sentry was never initialized).
    Sentry.captureException(error, {
      extra: { componentStack: info?.componentStack },
      tags: { error_boundary: "root" },
    });
  }
  handleReload = () => window.location.reload();
  handleHome = () => { window.location.href = "/"; };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div data-testid="error-boundary" className="min-h-screen grid place-items-center bg-background px-6 text-center">
        <div className="max-w-md">
          <div className="mx-auto mb-5 grid place-items-center w-14 h-14 rounded-full bg-primary/15 text-primary">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-slate-100">Something went wrong</h1>
          <p className="text-sm text-slate-400 mt-2">
            The page hit an unexpected error. Your work is saved — you can reload this page or head back to the home screen to keep going.
          </p>
          <div className="flex items-center justify-center gap-2 mt-6">
            <button data-testid="error-reload-btn" onClick={this.handleReload}
              className="inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 transition-colors">
              Reload page
            </button>
            <button data-testid="error-home-btn" onClick={this.handleHome}
              className="inline-flex items-center rounded-full border border-white/15 bg-secondary px-5 py-2.5 text-sm font-semibold text-slate-100 hover:bg-slate-700 transition-colors">
              Go to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
